<template>
  <section 
    id="about" 
    class="w-full relative py-20 overflow-hidden font-poppins bg-[#FAF9F7]"
    style="position: relative; z-index: 5;"
  >
    <!-- Improved background with subtle texture -->
    <div class="absolute inset-0 opacity-5" style="background-image: url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23D9A68C\' fill-opacity=\'0.3\'%3E%3Cpath d=\'M36 34v6h6v-6h-6zm6 6v6h-6v-6h6zm-6-6v-6h6v6h-6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E');"></div>
    
    <!-- Smooth top transition -->
    <div class="absolute top-0 left-0 w-full h-16 bg-gradient-to-b from-[#FAF9F7] to-transparent"></div>
    <!-- Smooth bottom transition -->
    <div class="absolute bottom-0 left-0 w-full h-16 bg-gradient-to-t from-[#FAF9F7] to-transparent"></div>
    
    <div class="container mx-auto px-4 md:px-8 lg:px-16 max-w-[1600px] relative z-10">
      <!-- Section header with refined styling -->
      <div class="mb-16 text-center">
        <h2 class="text-4xl md:text-5xl font-bold text-[#333333] mb-4">About Me</h2>
        <div class="h-1 w-24 mx-auto bg-gradient-to-r from-[#D2919A] to-[#D2919A]/70 rounded-full"></div>
      </div>
      
      <!-- Main content with improved layout -->
      <div class="grid grid-cols-1 lg:grid-cols-5 gap-12">
        <!-- Left column - Portrait with subtle effects (2 columns) -->
        <div class="lg:col-span-2 relative">
          <!-- More seamless image integration without border -->
          <div class="relative overflow-hidden group">
            <!-- Image with enhanced hover effect -->
            <img 
              src="../assets/IMG_0394.png" 
              alt="Josh Terzi" 
              class="w-full h-auto relative z-10 transition-all duration-700 
                     group-hover:scale-[1.03] group-hover:brightness-105
                     rounded-xl shadow-[0_5px_30px_rgba(210,145,154,0.25)]" 
            />
            
            <!-- Soft highlight glow that animates on hover -->
            <div class="absolute -inset-1 bg-gradient-to-tr from-[#D2919A]/10 via-[#FAF9F7]/5 to-white/10 
                        rounded-xl blur-xl opacity-40 group-hover:opacity-70 
                        transition-all duration-700 group-hover:blur-2xl"></div>
            
            <!-- Natural photo frame effect -->
            <div class="absolute inset-0 rounded-xl pointer-events-none" 
                 style="box-shadow: inset 0 0 0 1px rgba(255,255,255,0.1), 
                         inset 0 0 40px 10px rgba(250,249,247,0.1);"></div>
            
            <!-- Subtle hover overlay gradient -->
            <div class="absolute inset-0 bg-gradient-to-t from-[#333333]/30 to-transparent 
                        opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-20 
                        rounded-xl"></div>
          </div>
          
          <!-- Quote with refined styling - floating design -->
          <div class="mt-10 relative p-7 rounded-xl bg-white/90 backdrop-blur-md shadow-sm transform 
                      -translate-y-6 mx-4 lg:mx-8">
            <div class="relative">
              <!-- Decorative quote mark -->
              <span class="absolute -top-4 -left-2 text-5xl text-[#D2919A]/20 font-serif">"</span>
              <p class="text-xl text-[#333333] italic">
                I am grateful to call Dorset my home after moving here at the age of 26. Before that, I spent a lot of time searching for a place where I felt the comfort of home.
              </p>
              <span class="absolute -bottom-6 right-0 text-5xl text-[#D2919A]/20 font-serif">"</span>
            </div>
            
            <!-- Subtle accent decoration -->
            <div class="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gradient-to-r 
                        from-transparent via-[#D2919A]/40 to-transparent rounded-full"></div>
          </div>
        </div>
        
        <!-- Right column - Background and journey (3 columns) -->
        <div class="lg:col-span-3 lg:pl-6">
          <h3 class="text-2xl font-bold text-[#333333] mb-6 inline-block relative">
            Background & Journey
            <!-- Replace bottom border with gradient -->
            <span class="absolute -bottom-1 left-0 w-full h-[2px] bg-gradient-to-r from-[#D2919A] to-[#D2919A]/70"></span>
          </h3>
          
          <div class="space-y-6 text-[#333333]">
            <p class="text-lg leading-relaxed">
              Born and raised in London, I spent much of my teenage years in boarding schools, searching for a place that felt like home.
            </p>
            
            <p class="text-lg leading-relaxed">
              As soon as I could work and save money, I decided that my ideal place must be out there somewhere. I lived in Australia and Vietnam, traveling extensively, but I still couldn't find what I was looking for.
            </p>
            
            <p class="text-lg leading-relaxed">
              My mental health struggles only became apparent in hindsight, along with the self-medication I began around the age of 15. This led to a decade of substance and behavioural addictions, which eventually required therapeutic rehabilitation.
            </p>
            
            <!-- Highlighted paragraph with special styling -->
            <p class="text-lg leading-relaxed italic font-medium text-[#333333] p-4 bg-[#D2919A]/5 border-l-4 border-[#D2919A]/30 rounded-r-md">
              That's when I discovered the magic of therapy. I had little idea of what therapy was or how it could help; I just knew I needed help. This journey began in 2018, and the gift of therapy is something I am very passionate about.
            </p>
            
            <p class="text-lg leading-relaxed">
              Since then, I have been diagnosed with ADHD, which I long suspected might have contributed to my substance use addiction. This diagnosis was a relief, as I had felt unheard for many years.
            </p>
          </div>
          
          <!-- Experience section with improved card design -->
          <div class="mt-12 pt-10 border-t border-[#D2919A]/20">
            <h3 class="text-2xl font-bold text-[#333333] mb-6 inline-block relative">
              Experience
              <span class="absolute -bottom-1 left-0 w-1/2 h-[2px] bg-gradient-to-r from-[#D2919A] to-[#D2919A]/70"></span>
            </h3>
            
            <div class="space-y-6">
              <!-- Experience item 1 with hover effects -->
              <div class="exp-card">
                <div class="flex items-start">
                  <div class="exp-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 class="exp-title">
                      Mentor
                      <span class="exp-title-line"></span>
                    </h4>
                    <p class="text-[#333333] text-base leading-relaxed">
                      After my time as a rehab client, I naturally transitioned into a mentor role, working with others new to recovery. I shared my journey and provided a listening ear in one-on-one settings.
                    </p>
                  </div>
                </div>
              </div>
              
              <!-- Experience item 2 -->
              <div class="exp-card">
                <div class="flex items-start">
                  <div class="exp-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                    </svg>
                  </div>
                  <div>
                    <h4 class="exp-title">
                      Charity Work
                      <span class="exp-title-line"></span>
                    </h4>
                    <p class="text-[#333333] text-base leading-relaxed">
                      Upon moving to Dorset, I began working with a local charity that supports children and young people in education and mental health.
                    </p>
                  </div>
                </div>
              </div>
              
              <!-- Experience item 3 -->
              <div class="exp-card">
                <div class="flex items-start">
                  <div class="exp-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <h4 class="exp-title">
                      Therapeutic Support
                      <span class="exp-title-line"></span>
                    </h4>
                    <p class="text-[#333333] text-base leading-relaxed">
                      Currently, I work for a frontline local drug and alcohol service in their young persons team, providing therapeutic support to under-18s affected by substance use, both directly and indirectly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { gsap } from '@gsap/shockingly'
import { ScrollTrigger } from '@gsap/shockingly/ScrollTrigger'

export default {
  name: 'AboutSection',
  data() {
    return {
      activeCategory: 'background', // Current tab
      isTabSwitching: false, // Whether tab switching animation is in progress
      animationsInitialized: false
    }
  },
  mounted() {
    // Initialize animations immediately
    this.initAnimations();
    
    // Force experience cards to be visible after a delay as a backup
    setTimeout(() => {
      document.querySelectorAll('.exp-card').forEach(card => {
        card.style.visibility = "visible";
        card.style.opacity = "1";
        card.style.transform = "translateY(0)";
      });
    }, 2000);
  },
  beforeUnmount() {
    window.removeEventListener('heroVideoLocked', this.initAnimations);
    
    // Clean up ScrollTrigger instances
    if (typeof ScrollTrigger !== 'undefined' && ScrollTrigger.getAll) {
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.vars.id && trigger.vars.id.startsWith('about-')) {
          trigger.kill();
        }
      });
    }
  },
  methods: {
    initAnimations() {
      if (this.animationsInitialized) return;
      this.animationsInitialized = true;
      
      console.log('Initializing AboutSection animations');
      
      // Only proceed if GSAP is available
      if (typeof gsap === 'undefined') return;
      
      // Animate the photo and quote with a subtle entrance
      const photoElement = document.querySelector('#about .lg\\:col-span-2 img');
      const quoteElement = document.querySelector('#about .lg\\:col-span-2 > div:last-child');
      
      if (photoElement) {
        gsap.from(photoElement, {
          y: 40,
          opacity: 0,
          scale: 0.95,
          duration: 1.2,
          ease: 'power3.out',
          scrollTrigger: {
            id: 'about-photo',
            trigger: photoElement,
            start: 'top 80%',
          }
        });
      }
      
      if (quoteElement) {
        gsap.from(quoteElement, {
          y: 30,
          opacity: 0,
          duration: 1,
          delay: 0.3,
          ease: 'power2.out',
          scrollTrigger: {
            id: 'about-quote',
            trigger: quoteElement,
            start: 'top 85%',
          }
        });
      }
      
      // Animate in the background section
      const expCards = document.querySelectorAll('.exp-card');
      if (expCards.length) {
        gsap.from(expCards, {
          opacity: 0,
          y: 30,
          stagger: 0.2,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            id: 'about-exp-cards',
            trigger: expCards[0],
            start: 'top 80%',
          }
        });
      }
      
      // Force re-render by directly showing the cards if animations fail
      setTimeout(() => {
        const expCardsCheck = document.querySelectorAll('.exp-card');
        expCardsCheck.forEach(card => {
          if (parseFloat(window.getComputedStyle(card).opacity) < 0.9) {
            card.style.opacity = "1";
            card.style.transform = "translateY(0)";
          }
        });
      }, 1500);
    }
  }
}
</script>

<style scoped>
/* Core section styling */
.section-title {
  @apply text-4xl md:text-5xl font-bold text-[#333333];
}

/* Experience cards styling */
.exp-card {
  @apply bg-white rounded-lg p-5 transition-all duration-300 relative overflow-hidden;
  border: 1px solid rgba(210, 145, 154, 0.15);
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);
  transform: translateY(0);
}

.exp-card:hover {
  transform: translateY(-3px);
  border-color: rgba(210, 145, 154, 0.3);
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.03);
}

.exp-icon {
  @apply w-12 h-12 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/70 flex items-center justify-center mr-4 flex-shrink-0 shadow-sm;
  transition: transform 0.3s ease;
}

.exp-card:hover .exp-icon {
  transform: scale(1.1);
}

.exp-title {
  @apply text-xl font-semibold text-[#333333] mb-2 relative inline-block pb-2;
  position: relative;
}

.exp-title-line {
  @apply absolute -bottom-px left-0 h-[2px] bg-gradient-to-r from-[#D2919A]/50 to-transparent;
  width: 2rem;
  transition: width 0.3s ease;
}

.exp-card:hover .exp-title-line {
  width: 3rem;
}

/* Smooth transitions */
.tab-panel {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

/* Shadow effect for text */
.shadow-text {
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
</style>